import { useUploadImage } from "@/services/profile.service";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import LoadingDots from "@/components/loaders/LoadingDots.tsx";

interface BannerUploadProps {
  imageUrl: string;
  onUpload: (url: string) => void;
  label: string;
  dimensions: string; // e.g. "1200x353 px"
}

export const BannerUpload = ({
  imageUrl,
  onUpload,
  label,
  dimensions,
}: BannerUploadProps) => {
  const { mutate } = useUploadImage();
  const [isLoading, setLoading] = useState(false);

  const uploadImage = (file: File) => {
    setLoading(true);
    mutate(file, {
      onSuccess: (res) => {
        const imageUrlResponse = res?.data?.data?.response;
        onUpload(imageUrlResponse); // Update parent state
        toast.success("Image uploaded successfully.");
        setLoading(false);
      },
      onError: () => {
        toast.error("Failed to upload image.");
        setLoading(false);
      },
    });
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];

      // Type check
      if (!file.type.startsWith("image/")) {
        toast.error("Only .jpeg, .png, .jpg, or .webp files are allowed.");
        return;
      }

      // Size check (limit: 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size exceeds 10MB.");
        return;
      }

      // Load image to check dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const [expectedWidth, expectedHeight] = dimensions
          .replace("px", "")
          .split("x")
          .map((val) => parseInt(val.trim(), 10));

        console.log(`expected dimensions: ${expectedWidth}x${expectedHeight}`);
        console.log(`current dimensions: ${img.width}x${img.height}`);

        // Check dimensions
        // if (img.width !== expectedWidth || img.height !== expectedHeight) {
        //   toast.error(`Image dimensions must be ${dimensions}.`);
        //   URL.revokeObjectURL(objectUrl);
        //   return;
        // }

        URL.revokeObjectURL(objectUrl);
        uploadImage(file);
      };

      img.onerror = () => {
        toast.error("Failed to load image for dimension check.");
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    },
    [dimensions, uploadImage],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
  });

  const handleRemoveImage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onUpload(""); // Remove image
  };

  return (
    <div className="flex flex-col items-start">
      <p className="mb-2 text-sm font-semibold">{label}</p>
      <div
        {...getRootProps()}
        className="from-blue-500 relative flex h-40 w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-[#555555] bg-gradient-to-r to-indigo-500"
      >
        {isLoading ? (
          <LoadingDots />
        ) : imageUrl ? (
          <>
            <img
              src={imageUrl}
              className="h-full w-full rounded-lg object-cover"
              alt="Uploaded"
            />
            <button
              onClick={handleRemoveImage}
              className="shadow-md absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
            >
              ✖
            </button>
          </>
        ) : (
          <p className="text-white">
            {isDragActive
              ? "Drop to upload"
              : "Drag or Click to upload image file"}
          </p>
        )}
        <input {...getInputProps()} />
      </div>
      <p className="mt-2 text-xs text-gray-400">
        You can upload .jpeg, .png, .jpg, or .webp files. Max size: 10MB.
      </p>
      <p className="text-xs text-gray-400">Space dimension is {dimensions}.</p>
    </div>
  );
};
